import { motion } from "framer-motion";
import { Star, Quote } from "lucide-react";
import { REVIEWS } from "../lib/brand";
import { useT } from "../lib/i18n";

const FEATURED = REVIEWS.slice(0, 6);

export default function Testimonials() {
  const { t } = useT();
  return (
    <section
      id="reviews"
      className="bg-[color:var(--bg-elev)] py-20 md:py-28 relative"
      data-testid="testimonials-section"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-16">
        {/* Auto-scrolling top strip of reviews */}
        <div className="mb-12 md:mb-16 -mx-6 md:-mx-10 lg:-mx-16 py-3 border-y border-white/10 overflow-hidden">
          <div className="marquee-slow-track flex gap-10 will-change-transform">
            {[...REVIEWS, ...REVIEWS].map((r, i) => (
              <div key={i} className="flex items-center gap-3 shrink-0" data-testid={`review-item-${i}`}>
                <div className="flex gap-0.5 text-[color:var(--primary)]">
                  {Array.from({ length: r.rating }).map((_, k) => (
                    <Star key={k} size={11} fill="currentColor" strokeWidth={0} />
                  ))}
                </div>
                <p className="text-xs md:text-sm text-white/85">
                  <span className="italic">&ldquo;{r.text}&rdquo;</span>
                  <span className="ml-2 text-white/50 text-[10px] uppercase tracking-widest">
                    — {r.name}, {r.city}
                  </span>
                </p>
                <span className="text-[color:var(--primary)] text-sm">✦</span>
              </div>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-12 gap-6 md:gap-10 mb-12 md:mb-14 items-end">
          <div className="md:col-span-8">
            <p className="overline mb-3">{t("testimonials.overline")}</p>
            <h2 className="font-display text-white text-4xl md:text-5xl lg:text-6xl leading-[0.95]">
              {t("testimonials.heading")}
            </h2>
          </div>
          <div className="md:col-span-4">
            <p className="text-sm md:text-base text-white/60">
              {t("testimonials.subhead")}
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {FEATURED.map((r, i) => (
            <motion.figure
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: i * 0.04 }}
              className="relative bg-black border border-white/10 p-6 md:p-7 hover:border-[color:var(--primary)]/60 transition-colors"
              data-testid={`testimonial-card-${i}`}
            >
              <Quote size={22} className="text-[color:var(--primary)] mb-3" strokeWidth={1.5} />
              <blockquote className="font-body text-white text-base md:text-[17px] leading-relaxed">
                &ldquo;{r.text}&rdquo;
              </blockquote>
              <figcaption className="mt-5 flex items-center justify-between border-t border-white/10 pt-3">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.14em] text-white">{r.name}</p>
                  <p className="text-[10px] text-white/50 uppercase tracking-widest font-mono">
                    {r.city}
                  </p>
                </div>
                <div className="flex gap-0.5 text-[color:var(--primary)]">
                  {Array.from({ length: r.rating }).map((_, k) => (
                    <Star key={k} size={12} fill="currentColor" strokeWidth={0} />
                  ))}
                </div>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
}
